import { useEffect, useMemo, useState } from 'react';
import type { ProfileNetworkSettings, ProfileStats } from '../types/profile';
import { createUnknownProfileStats } from '../types/profile';
import { fetchProfileStats } from '../services/api/profileStatsService';

type UseProfileStatsResult = {
  stats: ProfileStats;
  isLoading: boolean;
  error?: string;
  effectiveTimezone: string;
};

const statsCache = new Map<string, ProfileStats>();

export function useProfileStats(
  userId?: string,
  networkSettings?: ProfileNetworkSettings | null,
  fallbackTimezone?: string
): UseProfileStatsResult {
  const resolvedFallbackTimezone = useMemo(
    () => fallbackTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [fallbackTimezone]
  );

  const effectiveTimezone = (
    networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || resolvedFallbackTimezone
  );
  const cacheKey = `${userId || 'anonymous'}::${effectiveTimezone}`;

  const [stats, setStats] = useState<ProfileStats>(createUnknownProfileStats());
  const [isLoading, setIsLoading] = useState<boolean>(!!userId);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!userId) {
      setStats(createUnknownProfileStats());
      setIsLoading(false);
      setError(undefined);
      return;
    }

    const cached = statsCache.get(cacheKey);
    if (cached) {
      setStats(cached);
      setIsLoading(false);
      setError(undefined);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    setIsLoading(true);
    setError(undefined);

    fetchProfileStats({
      userId,
      effectiveTimezone,
      signal: controller.signal,
    })
      .then((nextStats) => {
        if (!isMounted) {
          return;
        }
        statsCache.set(cacheKey, nextStats);
        setStats(nextStats);
        setIsLoading(false);
      })
      .catch((nextError: Error) => {
        if (!isMounted) {
          return;
        }
        setStats(createUnknownProfileStats());
        setError(nextError.message || 'Failed to load profile stats.');
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [cacheKey, effectiveTimezone, userId]);

  return { stats, isLoading, error, effectiveTimezone };
}
